// src/controllers/services/dbMapping.js

/**
 * Maps a Supabase Database Property row to the structure expected by the React components.
 */
/**
 * Review status of a listing, as lower-case text: 'approved', 'pending',
 * 'rejected', 'sold' or 'rented'. The database has no "rejected" status; a
 * rejected listing is stored as `pending` with a rejection reason. The mobile
 * app and the admin panel read it the same way.
 */
export const getReviewStatus = (dbProp) => {
  if (!dbProp) return 'pending';
  if (dbProp.status === 'pending' && dbProp.rejection_reason) return 'rejected';
  return dbProp.status || 'pending';
};

/**
 * Lead / enquiry status as shown to people. The mobile app and the admin
 * panel use the same labels. 'accepted' and 'rejected' come from older builds.
 */
export const getLeadStatusLabel = (status) => {
  switch ((status || '').toLowerCase()) {
    case '':
    case 'pending':
    case 'new':
      return 'New';
    case 'accepted':
    case 'contacted':
      return 'Contacted';
    case 'visit_scheduled':
      return 'Visit scheduled';
    case 'closed':
      return 'Closed';
    case 'rejected':
    case 'not_interested':
      return 'Not interested';
    default:
      return status.replace(/_/g, ' ');
  }
};

export const mapDbPropertyToUi = (dbProp) => {
  if (!dbProp) return null;

  // Format Price: Numeric e.g., 45000000 -> "₹4.5 Cr", 350000 -> "₹3.5 Lacs", 15000 -> "₹15,000"
  let priceStr = '';
  const price = parseFloat(dbProp.price);
  if (price >= 10000000) {
    priceStr = `₹${(price / 10000000).toFixed(1)} Cr`;
  } else if (price >= 100000) {
    priceStr = `₹${(price / 100000).toFixed(1)} Lac`;
  } else {
    priceStr = `₹${price.toLocaleString()}`;
  }

  // Format Area: e.g., 1800 -> "1,800 sq.ft"
  const areaVal = parseFloat(dbProp.area_sq_ft);
  const areaStr = (!isNaN(areaVal) && areaVal > 0) 
    ? `${areaVal % 1 === 0 ? areaVal.toLocaleString('en-IN') : areaVal.toFixed(1)} sq.ft` 
    : '';

  // Format Type: Suppress "0 BHK" for non-residential plots/commercial
  const isNonBhk = /plot|land|commercial|shop|office|warehouse|industrial|showroom/i.test(dbProp.property_type || '');
  let typeStr = '';
  if (!isNonBhk && dbProp.bedrooms && Number(dbProp.bedrooms) > 0) {
    typeStr = `${dbProp.bedrooms} BHK ${dbProp.property_type || 'Apartment'}`;
  } else if (isNonBhk) {
    typeStr = dbProp.property_type || 'Commercial / Plot';
  } else if (dbProp.bedrooms && Number(dbProp.bedrooms) > 0) {
    typeStr = `${dbProp.bedrooms} BHK`;
  } else {
    typeStr = dbProp.property_type || 'Residential';
  }

  // Default fallback image if none provided
  const mainImage = (dbProp.images && dbProp.images.length > 0) 
    ? dbProp.images[0] 
    : 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';

  const galleryImages = (dbProp.images && dbProp.images.length > 0)
    ? dbProp.images
    : [mainImage];

  return {
    id: dbProp.id,
    name: dbProp.title || 'Untitled Property',
    location: dbProp.location || '',
    price: priceStr,
    rawPrice: dbProp.price,
    area: areaStr,
    rawArea: dbProp.area_sq_ft,
    type: typeStr,
    property_type: dbProp.property_type || 'Apartment',
    image: mainImage,
    gallery: galleryImages,
    builder: dbProp.users?.full_name || 'Owner',
    status: dbProp.status === 'approved' ? 'Approved' : (dbProp.status || 'Available'),
    reviewStatus: getReviewStatus(dbProp),
    rejectionReason: dbProp.rejection_reason || '',
    rejectionRemarks: dbProp.rejection_remarks || '',
    description: dbProp.description || '',
    amenities: dbProp.amenities || [],
    owner_id: dbProp.owner_id,
    latitude: dbProp.latitude ? parseFloat(dbProp.latitude) : null,
    longitude: dbProp.longitude ? parseFloat(dbProp.longitude) : null,
    is_for_sale: dbProp.is_for_sale ?? true,
    society_name: dbProp.society_name || '',
    furnishing: dbProp.furnishing || '',
    city: dbProp.city || '',
    pincode: dbProp.pincode || '',
    deposit: dbProp.deposit || null,
    direct_contact: Boolean(dbProp.direct_contact),
    offer_serial: dbProp.offer_serial || null,
  };
};

/**
 * Maps a Supabase Database Property Reel row to the structure expected by the Reels HUD.
 */
export const mapDbReelToUi = (dbReel, likedReelIds = [], savedReelIds = []) => {
  if (!dbReel) return null;

  return {
    id: dbReel.id,
    property_id: dbReel.property_id,
    owner_id: dbReel.owner_id,
    video_url: dbReel.video_url || '',
    thumbnail_url: dbReel.thumbnail_url || 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    caption: dbReel.caption || '',
    music_url: dbReel.music_url || '',
    music_title: dbReel.music_title || 'Original Sound',
    views_count: dbReel.views_count || 0,
    likes_count: dbReel.likes_count || 0,
    saves_count: dbReel.saves_count || 0,
    isLiked: likedReelIds.includes(dbReel.id),
    isSaved: savedReelIds.includes(dbReel.id),
    property: mapDbPropertyToUi(dbReel.properties)
  };
};

/**
 * Maps a Chat room row to a UI format.
 */
export const mapDbChatToUi = (dbChat, currentUserId) => {
  if (!dbChat) return null;

  // Determine counterpart user (the one who is not the current user)
  const counterpart = dbChat.buyer_id === currentUserId ? dbChat.sellers : dbChat.buyers;
  
  return {
    id: dbChat.id,
    property_id: dbChat.property_id,
    property_name: dbChat.properties?.title || 'Unknown Property',
    counterpart_id: counterpart?.id || '',
    counterpart_name: counterpart?.full_name || 'User',
    counterpart_photo: counterpart?.profile_picture_url || '',
    counterpart_phone: counterpart?.phone_number || '',
    last_message: dbChat.messages && dbChat.messages.length > 0 ? dbChat.messages[0].content : '',
    last_message_time: dbChat.messages && dbChat.messages.length > 0 ? dbChat.messages[0].created_at : dbChat.created_at,
  };
};
